import React, { useState } from 'react';
import { LinkMetadata } from '../types';
import { Globe, MessageCircle, Share2, Info } from 'lucide-react';
import { cn } from '../lib/utils';
import { getCurrentDomain } from '../lib/domain';

const TwitterIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface SocialPreviewCardProps {
  title: string;
  destinationUrl: string;
  metadata?: LinkMetadata | null;
  shortCode?: string;
}

export const SocialPreviewCard: React.FC<SocialPreviewCardProps> = ({
  title,
  destinationUrl,
  metadata,
  shortCode = 'preview',
}) => {
  const [activePlatform, setActivePlatform] = useState<'whatsapp' | 'facebook' | 'twitter'>('whatsapp');

  const ogTitle = metadata?.og_title || metadata?.meta_title || title || 'AI Builder Challenge — Brototype';
  const ogDescription =
    metadata?.og_description ||
    metadata?.meta_description ||
    'Build software applications using AI with mentorship and free technical support from Brototype.';
  const ogImage =
    metadata?.og_image_url ||
    metadata?.twitter_image_url ||
    'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&h=630&fit=crop';

  const currentDomain = getCurrentDomain();
  let destinationHost = currentDomain;
  try {
    if (destinationUrl) {
      destinationHost = new URL(destinationUrl).hostname;
    }
  } catch {
    destinationHost = currentDomain;
  }

  return (
    <div className="space-y-4">
      {/* Platform tabs */}
      <div className="flex items-center gap-1.5 p-1 bg-slate-900 border border-slate-800 rounded-xl max-w-sm">
        <button
          type="button"
          onClick={() => setActivePlatform('whatsapp')}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer',
            activePlatform === 'whatsapp'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200'
          )}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span>WhatsApp</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePlatform('facebook')}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer',
            activePlatform === 'facebook'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200'
          )}
        >
          <Share2 className="w-3.5 h-3.5" />
          <span>Facebook</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePlatform('twitter')}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer',
            activePlatform === 'twitter'
              ? 'bg-slate-700 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200'
          )}
        >
          <TwitterIcon className="w-3.5 h-3.5" />
          <span>X / Twitter</span>
        </button>
      </div>

      {/* Realistic Preview Simulation Container */}
      <div className="p-4 sm:p-6 rounded-2xl bg-slate-950/70 border border-slate-800/80 flex items-center justify-center">
        {/* WhatsApp Preview */}
        {activePlatform === 'whatsapp' && (
          <div className="max-w-sm w-full bg-[#0b141a] rounded-xl p-3 shadow-2xl border border-emerald-900/30">
            <div className="bg-[#1f2c34] rounded-lg overflow-hidden border border-slate-800 shadow-md">
              <div className="h-44 w-full bg-slate-800 relative overflow-hidden">
                <img
                  src={ogImage}
                  alt={ogTitle}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
              </div>
              <div className="p-3">
                <h4 className="text-sm font-semibold text-slate-100 line-clamp-1">{ogTitle}</h4>
                <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">{ogDescription}</p>
                <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500">
                  <Globe className="w-3 h-3 text-slate-500" />
                  <span>{currentDomain}/{shortCode}</span>
                </div>
              </div>
            </div>
            <div className="mt-2 text-[10px] text-right text-slate-500 font-mono">11:42 AM &bull; Read</div>
          </div>
        )}

        {/* Facebook / LinkedIn Preview */}
        {activePlatform === 'facebook' && (
          <div className="max-w-md w-full bg-[#242526] rounded-xl overflow-hidden shadow-2xl border border-slate-800">
            <div className="h-48 w-full bg-slate-800 relative overflow-hidden">
              <img
                src={ogImage}
                alt={ogTitle}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            </div>
            <div className="p-3.5 bg-[#3a3b3c]/60">
              <span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium font-mono">
                {currentDomain}
              </span>
              <h4 className="text-sm font-bold text-slate-100 mt-0.5 line-clamp-1">{ogTitle}</h4>
              <p className="text-xs text-slate-300 mt-1 line-clamp-2 leading-snug">{ogDescription}</p>
            </div>
          </div>
        )}

        {/* X / Twitter Card Preview */}
        {activePlatform === 'twitter' && (
          <div className="max-w-md w-full bg-black rounded-2xl overflow-hidden shadow-2xl border border-slate-800">
            <div className="h-48 w-full bg-slate-900 relative overflow-hidden">
              <img
                src={ogImage}
                alt={ogTitle}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            </div>
            <div className="p-3.5 bg-black border-t border-slate-800/80">
              <span className="text-xs text-slate-500 font-mono">{currentDomain}</span>
              <h4 className="text-sm font-semibold text-slate-100 mt-0.5 line-clamp-1">{ogTitle}</h4>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{ogDescription}</p>
            </div>
          </div>
        )}
      </div>

      {/* Accuracy notice */}
      <div className="flex items-start gap-2 p-3 rounded-xl bg-slate-900/40 border border-slate-800 text-[11px] text-slate-400">
        <Info className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
        <p>
          Estimated visual rendering. Actual link card appearance depends on the respective social platform crawler's cache and presentation policies.
        </p>
      </div>
    </div>
  );
};
